// Perfil, tema, estilo dos botoes e redes sociais do link na bio.

import {
  BIO_BUTTON_LABELS,
  BIO_BUTTON_RADIUS,
  BIO_BUTTON_STYLES,
  BIO_THEMES,
  BIO_THEME_LABELS,
  BIO_THEME_PALETTES,
  MAX_BIO_DESCRIPTION,
  MAX_BIO_TITLE,
  SOCIAL_LABELS,
  SOCIAL_NETWORKS,
  type BioPageSettings,
} from "@lucralink/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface Props {
  readonly settings: BioPageSettings;
  readonly onChange: (next: BioPageSettings) => void;
}

export function BioProfileFields({ settings, onChange }: Props): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="bio-title" variant="field">Título</Label>
        <Input id="bio-title" value={settings.title} maxLength={MAX_BIO_TITLE} placeholder="Seu nome ou marca" onChange={(e) => onChange({ ...settings, title: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="bio-desc" variant="field">Descrição</Label>
        <textarea
          id="bio-desc"
          rows={2}
          maxLength={MAX_BIO_DESCRIPTION}
          value={settings.description}
          onChange={(e) => onChange({ ...settings, description: e.target.value })}
          className="flex w-full rounded-md border bg-[hsl(var(--input))] px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="bio-logo" variant="field">Logo (URL https)</Label>
        <Input id="bio-logo" type="url" value={settings.logoUrl ?? ""} placeholder="https://..." onChange={(e) => onChange({ ...settings, logoUrl: e.target.value || null })} />
      </div>
    </div>
  );
}

export function BioStyleFields({ settings, onChange }: Props): JSX.Element {
  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="Tema" className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {BIO_THEMES.map((theme) => (
          <button
            key={theme}
            type="button"
            role="radio"
            aria-checked={settings.theme === theme}
            onClick={() => onChange({ ...settings, theme })}
            className={cn("flex items-center gap-2 rounded-lg border p-2 text-xs", settings.theme === theme ? "border-primary/50 bg-primary/10" : "border-border")}
          >
            <span className="h-5 w-5 rounded-full border border-border" style={{ background: `linear-gradient(135deg, ${BIO_THEME_PALETTES[theme].bg} 50%, ${BIO_THEME_PALETTES[theme].accent} 50%)` }} />
            {BIO_THEME_LABELS[theme]}
          </button>
        ))}
      </div>
      <div role="radiogroup" aria-label="Estilo dos botões" className="grid grid-cols-3 gap-2">
        {BIO_BUTTON_STYLES.map((style) => (
          <button
            key={style}
            type="button"
            role="radio"
            aria-checked={settings.buttonStyle === style}
            onClick={() => onChange({ ...settings, buttonStyle: style })}
            className={cn("border px-3 py-2 text-xs font-medium", settings.buttonStyle === style ? "border-primary/50 bg-primary/10 text-primary" : "border-border")}
            style={{ borderRadius: BIO_BUTTON_RADIUS[style] }}
          >
            {BIO_BUTTON_LABELS[style]}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BioSocialFields({ settings, onChange }: Props): JSX.Element {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {SOCIAL_NETWORKS.map((network) => (
        <Input
          key={network}
          aria-label={SOCIAL_LABELS[network]}
          type="url"
          placeholder={`${SOCIAL_LABELS[network]} (https://...)`}
          value={settings.socials[network] ?? ""}
          onChange={(e) => onChange({ ...settings, socials: { ...settings.socials, [network]: e.target.value } })}
        />
      ))}
    </div>
  );
}
